import { supabaseClient } from '@/services/supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { isNonNullable } from './nullability-helpers';
import type { PossibleLanguage, TranslationKey } from '@/i18n';
import { FrontendError } from './error-helpers';
import type { FrontendScenarioDefinition } from '@shared-types/frontend-scenario-definition';
import type { PeriodKey, PublicScenarioDetails } from '@shared-types/scenario-definition';
import type { FrontendSimulationMetadata } from '@/types/simulation';
export class EdgeFunctionsManager {
    static getParticipantSignInData = (
        accessCode: string,
    ): Promise<{ email: string; otp: string; simulationID: string }> =>
        this.invokeEdgeFunction('get-participant-sign-in-data', { accessCode });

    static requestAccessLink = (
        email: string,
        language: PossibleLanguage,
        name?: string,
        organization?: string,
    ): Promise<string> => this.invokeEdgeFunction('request-access-link', { email, name, organization, language });

    static createSimulation = async (
        simulationMetadata: FrontendSimulationMetadata,
        organizationId: string,
    ): Promise<{ id: string }> => this.invokeEdgeFunction('create-simulation', { simulationMetadata, organizationId });

    static editSimulation = async (
        simulationID: string,
        simulationMetadata: FrontendSimulationMetadata,
    ): Promise<void> => this.invokeEdgeFunction('edit-simulation', { simulationID, simulationMetadata });

    static calculateNextPeriod = (simulationID: string, currentPeriodKey: PeriodKey): Promise<void> =>
        this.invokeEdgeFunction('calculate-next-period', { simulationID, currentPeriodKey });

    static startPeriod = (simulationID: string): Promise<void> =>
        this.invokeEdgeFunction('start-period', { simulationID });

    static sendParticipantInviteEmails = async (simulationID: string, language: PossibleLanguage, sendToAll: boolean) =>
        this.invokeEdgeFunction('send-invite-emails', { simulationID, sendToAll, language });

    static sendPasswordResetEmail = (email: string, language: PossibleLanguage): Promise<void> =>
        this.invokeEdgeFunction('send-reset-password-email', { email, language });

    static changeEmail = (email: string, newEmail: string, language: PossibleLanguage): Promise<void> =>
        this.invokeEdgeFunction('send-change-email-email', { email, newEmail, language });

    static getPossibleScenarios = (): Promise<PublicScenarioDetails[]> =>
        this.invokeEdgeFunction('get-possible-scenarios');

    static getScenarioDefinition = (
        body: { simulationID: string; language?: string } | { scenarioID: string; language?: string },
    ): Promise<FrontendScenarioDefinition> => this.invokeEdgeFunction('get-scenario-definition', body);

    static verifyJwt = (jwt: string): Promise<{ token: string; email: string; simulationId: string }> =>
        this.invokeEdgeFunction('verify-jwt', { jwt });

    static invokeEdgeFunction = async (name: string, body?: Record<string, any>) => {
        const { data, error } = await supabaseClient.functions.invoke(name, { body });
        if (isNonNullable(error)) {
            if (error instanceof FunctionsHttpError) {
                const {
                    error: { code },
                } = (await error.context.json()) as { error: { code: string } };
                throw new FrontendError(`errors.edgeFunctions.${code}` as TranslationKey);
            } else {
                throw error;
            }
        }
        return data;
    };
}
